import { useEffect, useMemo, useRef } from 'react';
import {
  Button,
  CheckboxField,
  FormContextProvider,
  InputField,
  ListboxField,
  SearchSelectField,
  Textarea,
} from '@nbirl/nbi-common-ui-libs';
import { Form, FormikHelpers } from 'formik';

import { useFolderUsersQuery } from '../../../../queries';
import {
  shareDocumentValidationSchema,
  SUBJECTS,
  subjectToString,
} from './share-document-form.form';
import { ShareDocumentFormValues } from './share-document-form.model';

type ShareDocumentFormProps = {
  documentId: string;
  folderId: string;
  isSubmitting: boolean;
  isPending?: boolean;
  onSubmit: (
    values: ShareDocumentFormValues,
    formikHelpers: FormikHelpers<ShareDocumentFormValues>
  ) => void;
  onPassButtonRef: (ref: React.RefObject<HTMLButtonElement>) => void;
};

export const ShareDocumentForm = ({
  documentId,
  folderId,
  isPending,
  isSubmitting,
  onSubmit,
  onPassButtonRef,
}: ShareDocumentFormProps) => {
  const shareUsersQuery = useFolderUsersQuery(folderId, documentId);
  const sendLinkButtonRef = useRef<HTMLButtonElement>(null);

  const sendToFieldOptions = useMemo(
    () =>
      shareUsersQuery.data?.map((u) => ({
        label: u.name,
        value: u.id,
        helpText: u.email,
      })) || [],
    [shareUsersQuery.data]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => onPassButtonRef(sendLinkButtonRef), []);

  return (
    <FormContextProvider<ShareDocumentFormValues>
      initialValues={{
        subject: isPending ? '' : 'SHARE_DOCUMENT',
        message: '',
        sendCopy: false,
        to: [],
      }}
      validationSchema={shareDocumentValidationSchema}
      onSubmit={onSubmit}
    >
      <Form className="space-y-3">
        <SearchSelectField
          name="to"
          showRequiredIndicator={false}
          placeholder="Add people"
          options={sendToFieldOptions}
          isLoading={shareUsersQuery.isLoading}
        />
        <ListboxField
          disabled={!isPending}
          showRequiredIndicator={false}
          name="subject"
          placeholder="Purpose of this email"
          items={SUBJECTS}
          itemToString={subjectToString}
        />
        <InputField name="message" label="Message (optional)">
          {(field) => (
            <Textarea
              rows={3}
              placeholder="Add a note for the people you’re sharing this document with"
              {...field}
            />
          )}
        </InputField>
        <CheckboxField name="sendCopy" />
        <div className="flex justify-end">
          <Button
            ref={sendLinkButtonRef}
            isPending={isSubmitting}
            type="submit"
          >
            Share
          </Button>
        </div>
        <hr className="border-solid border-1 border-gray-100" />
      </Form>
    </FormContextProvider>
  );
};
